import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';

import { PresentationAV } from '@/components/ui/features/presentation/PresentationAV/PresentationAV';
import { PresentationEventTypes } from '@/components/ui/features/presentation/PresentationEventTypes/PresentationEventTypes';
import { PresentationHeader } from '@/components/ui/features/presentation/PresentationHeader/PresentationHeader';
import { PresentationPartners } from '@/components/ui/features/presentation/PresentationPartners/PresentationPartners';
import { PresentationReins } from '@/components/ui/features/presentation/PresentationReins/PresentationReins';
import { PresentationReviews } from '@/components/ui/features/presentation/PresentationReviews/PresentationReviews';
import { PresentationVideo } from '@/components/ui/features/presentation/PresentationVideo/PresentationVideo';
import { UnforgettableConference } from '@/components/ui/features/presentation/UnforgettableConference/UnforgettableConference';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { JsonLd } from '@/components/ui/shared/seo/JsonLd/JsonLd';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryTestimonials } from '@/queries/testimonals';
import { generateStore } from '@/utils/generateStore';
import { buildBreadcrumbSchema, buildWebPageSchema, getAbsoluteUrl } from '@/utils/structured-data';
import { Hydrate } from '@/utils/swr/Hydrate';

const PAGE_PATH = 'events';
const PAGE_TITLE = 'Professional Scientific Conference Organizer';
const PAGE_DESCRIPTION =
  'Virtual, hybrid, or live - whatever your need, our flexible solutions cater to it. Our comprehensive range of services ensures your conference realizes its full potential, regardless of the format.';

const Presentation: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Hydrate fallback={fallback}>
      <Layout accessFrom={AccessFrom.Core} navigationVariant="default">
        <NextSeo
          title={PAGE_TITLE}
          description={PAGE_DESCRIPTION}
          canonical={getAbsoluteUrl(PAGE_PATH)}
          openGraph={{
            url: getAbsoluteUrl(PAGE_PATH),
            title: PAGE_TITLE,
            description: PAGE_DESCRIPTION,
          }}
        />
        <JsonLd
          id="events"
          schema={[
            buildWebPageSchema({ path: PAGE_PATH, name: PAGE_TITLE, description: PAGE_DESCRIPTION }),
            buildBreadcrumbSchema([
              { name: 'Home', path: '' },
              { name: 'Events', path: PAGE_PATH },
            ]),
          ]}
        />
        <PresentationHeader />
        <PresentationPartners />
        <PresentationReviews />
        <UnforgettableConference />
        <PresentationVideo />
        <PresentationEventTypes />
        <PresentationAV />
        <PresentationReins />
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  await store.fetchResources(queryTestimonials());

  return {
    props: {
      fallback: store.fallback,
    },
  };
};
export default Presentation;
